<template>
  <div class="flex flex-col gap-4">
    <h1 class="text-text-primary text-2xl font-semibold flex items-center gap-2">
      {{ $t('common.maps') }}
      <div class="has-tooltip">
        <img src="../../../assets/icons/info-icon.svg" class="w-4 h-4">
        <span class="tooltip rounded shadow-md text-left p-3 bg-gray-800 text-white text-xs -mt-8 ml-5">
          {{ $t('coach.maps.tooltip') }}
        </span>
      </div>
    </h1>
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-y-4 lg:gap-6">
      <div class="flex flex-col gap-3 flex-1">
        <!-- Best map -->
        <MapCard v-if="bestMap" :label="$t('coach.maps.best')" :map="bestMap" labelClass="bg-success-900 text-success-400" />
        <!-- Worst map -->
        <div v-if="worstMap" class="flex flex-col">
          <MapCard :map="worstMap" :label="$t('coach.maps.worst')" labelClass="bg-danger-900 text-danger-400">
            <router-link
              :to="`/bootcamp/${getBootcamp(worstMap?.map_name)}`"
              class="text-text-primary bg-primary-600 hover:bg-primary-500 duration-300 z-50 text-sm px-4 py-2 rounded-lg flex justify-center items-center gap-2"
            >
              <h1>{{ $t('coach.maps.master_map') }}</h1>
              <img src="../../../assets/icons/rightarrow.svg" class="w-4 h-4 brightness-200" />
            </router-link>
          </MapCard>
        </div>
      </div>

      <!-- Map list table -->
      <div class="divide-y divide-gray-950 bg-gray-900 rounded-lg col-span-2 overflow-y-auto flex-1 max-h-[447px]">
        <table class="w-full divide-y divide-gray-950">
          <thead class="sticky top-0 bg-gray-900">
            <tr>
              <th class="text-text-primary font-medium text-left px-4 py-3 flex-1 flex gap-2 items-center">{{ $t('common.maps') }}</th>
              <th class="text-text-secondary font-medium tracking-[1.5px] text-xs uppercase text-right px-4 py-3">
                <div class="flex gap-2 items-center justify-end">
                  {{ $t('coach.maps.performance') }}
                  <div class="has-tooltip">
                    <img src="../../../assets/icons/info-icon.svg" class="w-4 h-4">
                    <span class="tooltip rounded shadow-md text-left p-3 bg-gray-800 text-white text-xs -mt-10 ml-2">
                      {{ $t('coach.maps.performance_tooltip') }}
                    </span>
                  </div>
                </div>
              </th>
              <th class="text-text-secondary font-medium tracking-[1.5px] text-xs uppercase text-right px-4 py-3">{{ $t('common.matches') }}</th>
              <th class="text-text-secondary font-medium tracking-[1.5px] text-xs uppercase text-right px-4 py-3">{{ $t('coach.maps.win_rate') }}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-950 w-full">
            <tr v-for="(map, index) in maps" :key="index" class="p-4 w-full gap-2 items-center">
              <td class="text-text-secondary text-left p-4 flex gap-2 items-center flex-1">
                <img :src="`/maps/map_icon_${map.map_name.toLowerCase()}.svg`" class="w-6 h-6" />
                {{ map.map_name_friendly }}
              </td>
              <td class="text-right p-4" :class="performanceColor(map)">{{ performanceLabel(map) }}</td>
              <td class="text-text-primary text-right p-4">{{ map.match_count }}</td>
              <td class="text-right p-4" :class="winRateColor(map.win_rate)">
                {{ (map.win_rate * 100).toFixed(0) }}%
              </td>
            </tr>
            <tr v-for="(map, index) in statlessMaps" :key="`empty-${index}`">
              <td class="text-text-secondary text-left p-4 flex gap-2 items-center flex-1">
                <img :src="`/maps/map_icon_${map.name.toLowerCase()}.svg`" class="w-6 h-6" />
                {{ map.friendly_name }}
              </td>
              <td class="text-text-secondary text-right p-4">{{ $t('coach.maps.not_available') }}</td>
              <td class="text-text-secondary text-right p-4">{{ $t('coach.maps.not_available') }}</td>
              <td class="text-text-secondary text-right p-4">{{ $t('coach.maps.not_available') }}</td>
            </tr>
            <tr v-if="maps?.length <= 3">
              <td colspan="4" class="text-text-secondary text-sm text-center p-4 font-light">
                {{ $t('coach.maps.more_maps_hint') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import MapCard from './MapCard.vue';

const { t } = useI18n();

defineProps({
  maps: Array,
  statlessMaps: Array,
  bestMap: Object,
  worstMap: Object,
});

function performanceTier(map) {
  const rating = map.averages?.hltv_rating;
  if (rating == null) return null;
  if (rating < 0.8) return 'poor';
  if (rating < 1.05) return 'average';
  if (rating <= 1.15) return 'good';
  return 'very_good';
}

function performanceLabel(map) {
  const tier = performanceTier(map);
  return tier ? t(`coach.maps.perf_${tier}`) : t('coach.maps.not_available');
}

function performanceColor(map) {
  const perf = performanceTier(map);
  if (perf === 'poor') return 'text-danger-400';
  if (perf === 'good' || perf === 'very_good') return 'text-success-400';
  return 'text-text-primary';
}

function winRateColor(rate) {
  if (rate > 0.5) return 'text-success-400';
  if (rate < 0.5) return 'text-danger-400';
  return 'text-text-primary';
}

function getBootcamp(mapName) {
  if (!mapName) return null;
  return mapName.replace('de_', '');
}
</script>
